import * as React from 'react';
import classnames from 'classnames';
import { isNil } from 'lodash';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { logError } from 'utils/log';

import './action-button-dropdown-menu-item.scss';

import type { ActionButtonDropdownItem, ActionButtonDropdownStyle } from 'types';

import type { SyntheticEvent } from 'react';

type Props = {
  item: ActionButtonDropdownItem,
  style?: ActionButtonDropdownStyle,
  withSelectable?: boolean,
  menuItemClassName?: string,
  onItemClick: (identifier: string) => void
};

class ActionButtonDropdownMenuItem extends React.PureComponent<Props> {
  itemRef: React.RefObject<any> = React.createRef();

  handleItemClick = (event: SyntheticEvent<HTMLElement>): void => {
    const { item, onItemClick } = this.props;
    const target = event.currentTarget;
    const identifier = target.dataset.identifier;

    // handle the result of `onMouseDown` being used instead of `onClick`
    event.preventDefault();

    if (!identifier) {
      logError('Empty identifier found for ', JSON.stringify(item));
    }
    onItemClick(identifier!);
  };

  renderIcon(): React.ReactNode | undefined | null {
    const { item } = this.props;
    const icon = item.icon;

    return !isNil(icon) && (
      <div className="action-button-dropdown-menu-item__icon">
        <SVGDynamicIcon
          name={icon.name}
          width={icon.width}
          height={icon.height}
          customColor={icon.color}
        />
      </div>
    );
  }

  renderLabel(): React.ReactNode {
    const { item } = this.props;

    return (
      <div className="action-button-dropdown-menu-item__label">
        {item.label}
      </div>
    );
  }

  render(): React.ReactNode {
    const { item, style, withSelectable, menuItemClassName } = this.props;

    const menuItemClasses = classnames('action-button-dropdown-menu-item', menuItemClassName, {
      [`action-button-dropdown-menu-item--style-${String(style)}`]: style,
      'action-button-dropdown-menu-item--selected': withSelectable && item.isSelected,
      'action-button-dropdown-menu-item--disabled': item.isDisabled
    });

    // reason for using `onMouseDown` instead of `onClick`
    // https://stackoverflow.com/a/28963938
    return (
      <div
        ref={this.itemRef}
        className={menuItemClasses}
        data-identifier={item.identifier}
        data-focused={item.isFocused}
        data-checked={item.isSelected}
        onMouseDown={this.handleItemClick}
      >
        {this.renderLabel()}
        {this.renderIcon()}
      </div>
    );
  }
}

export default ActionButtonDropdownMenuItem;
